import { useState } from "react";
import { ChevronRight, ChevronDown, Package } from "lucide-react";
import { AppInventoryItem } from "@/types/inventory";
import { TableCell, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface AppInventoryRowProps {
  app: AppInventoryItem;
  resolvedIcon?: string;
}

export const AppInventoryRow = ({ app, resolvedIcon }: AppInventoryRowProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const mostCommonVersion = app.versions.reduce((prev, current) => 
    (current.device_count > prev.device_count) ? current : prev
  , app.versions[0]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getIconSrc = (icon: string | null): string | null => {
    if (!icon) return null;
    if (icon.startsWith('data:image')) return icon;
    if (icon.startsWith('http')) return icon;
    if (icon.startsWith('/')) return icon;
    return null;
  };

  const iconSrc = getIconSrc(app.icon) || resolvedIcon || null;

  return (
    <>
      <TableRow 
        className="cursor-pointer hover:bg-muted/50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <TableCell>
          <div className="flex items-center gap-3">
            {iconSrc ? (
              <img 
                src={iconSrc} 
                alt={app.name}
                className="h-8 w-8 rounded object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <div className={cn("h-8 w-8 rounded bg-muted flex items-center justify-center", iconSrc && "hidden")}>
              <Package className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <div className="font-medium">{app.name}</div>
              <div className="text-sm text-muted-foreground">{app.bundle_id}</div>
            </div>
            {app.versions.length > 1 && (
              isExpanded ? (
                <ChevronDown className="ml-2 h-4 w-4 text-muted-foreground flex-shrink-0" />
              ) : (
                <ChevronRight className="ml-2 h-4 w-4 text-muted-foreground flex-shrink-0" />
              )
            )}
          </div>
        </TableCell>
        <TableCell>
          <Badge variant={app.is_managed ? "default" : "secondary"}>
            {app.is_managed ? "Managed" : "Unmanaged"}
          </Badge>
        </TableCell>
        <TableCell>
          {app.versions.length === 1 ? (
            app.versions[0].version
          ) : (
            <span className="text-muted-foreground">
              {mostCommonVersion.version} <span className="text-xs">({app.versions.length} versions)</span>
            </span>
          )}
        </TableCell>
        <TableCell className="text-right font-medium">{app.total_devices}</TableCell>
        <TableCell>{formatDate(app.first_seen)}</TableCell>
        <TableCell>{formatDate(app.last_updated)}</TableCell>
      </TableRow>
      
      {isExpanded && app.versions.length > 1 && app.versions.map((version, idx) => (
        <TableRow key={idx} className="bg-muted/20">
          <TableCell className="pl-16">
            <span className="text-sm text-muted-foreground">Version breakdown</span>
          </TableCell>
          <TableCell>
            <span className="text-sm text-muted-foreground">—</span>
          </TableCell>
          <TableCell>
            <span className="text-sm font-mono">{version.version}</span>
          </TableCell>
          <TableCell className="text-right">
            <span className="text-sm">{version.device_count} devices</span>
          </TableCell>
          <TableCell colSpan={2}></TableCell>
        </TableRow>
      ))}
    </>
  );
};
